import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl, statusList } from "../../../comman/config";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";

// Add/Edit Screen. A screen is one link in the side bar: a name, the route it opens, and whether
// it is active.
//
// Visible to roles and Sequence were here and have been taken off at the client's request. Both
// columns remain, and both still do their job -- roles gates who sees the screen, sequence orders
// it within its department -- they are simply no longer edited from this form. What they hold is
// preserved on save rather than overwritten with blanks, which is the whole reason the backend
// writes only the columns it is actually sent.
//
// Consequence worth knowing: a screen created here has no roles, and an empty roles column means
// EVERY role. A new screen is therefore visible to everyone until somebody narrows it, and there
// is no longer a field on this screen to narrow it with.
export default function ScreenMasterForm({ detail, page = 'screen_master' }) {

    const [formData, setFormData] = useState({
        name: '',
        path: '',
        menu_group_id: '',
        status: 1,
    });
    const [validationErrors, setValidationErrors] = useState({
        name: false,
        path: false,
    });
    // The side bar's headings. A screen sits under exactly one -- see backend/sql/menu_group.sql.
    const [menuGroupOptions, setMenuGroupOptions] = useState([]);
    const [saveError, setSaveError] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const formRef = useRef(null);

    const navigate = useNavigate();

    useEffect(() => {
        const getMenuGroups = async () => {
            try {
                const response = await axios.get(`${baseUrl}/comman/list?table=menu_group_master&condition=status=1`);
                setMenuGroupOptions((response.data?.data ?? []).map((group) => ({
                    value: group.id,
                    label: group.name,
                })));
            } catch (error) {
                console.log(error);
            }
        };
        getMenuGroups();
    }, []);

    useEffect(() => {
        if (detail) {
            const screen = detail[0];
            setFormData({
                name: screen.name || '',
                path: screen.path || '',
                menu_group_id: screen.menu_group_id ?? '',
                status: Number(screen.status ?? 1),
            });
        }
    }, [detail]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newValidationErrors = {
            name: formData.name.trim() === '',
            path: formData.path.trim() === '',
        };
        setValidationErrors(newValidationErrors);
        const hasErrors = Object.values(newValidationErrors).some((error) => error);

        if (hasErrors) {
            const firstInvalidInput = formRef.current.querySelector('.invalid');
            if (firstInvalidInput) {
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaveError('');
        try {
            setPopupOpen(true);
            const url = detail ? `${page}/edit/${detail[0].id}` : `${page}/add`;
            // Neither roles nor sequence is sent, and the backend leaves a column it was not
            // sent alone -- so a rename here does not empty a screen's role list or move it to
            // the top of its department. See screenUpdate in controllers/Master/ScreenMaster.js.
            await axios.post(`${baseUrl}/${url}`, { formData });
            setPopupOpen(false);
            navigate(`/${page}/list`);
        } catch (error) {
            setPopupOpen(false);
            console.log(error);
            // A duplicate path comes back as a 409 with the reason on it, and the reason is the
            // only thing that tells the user which field to change.
            setSaveError(error?.response?.data?.msg ?? 'Could not save the screen.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [name]: false
        }));
    };

    return (
        <form ref={formRef}>
            <div className="space-y-12">
                <div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
                        <div className="sm:col-span-2">
                            <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Screen Name *</label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    placeholder="Screen Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`block w-full rounded-md border ${validationErrors.name ? 'border-red-500 invalid' : 'border-gray-300'} py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {validationErrors.name && <div className="text-red-500">Please enter a screen name.</div>}
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="path" className="block text-sm font-medium leading-6 text-gray-900">Path *</label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="path"
                                    id="path"
                                    placeholder="/meal_master/list"
                                    value={formData.path}
                                    onChange={handleInputChange}
                                    className={`block w-full rounded-md border ${validationErrors.path ? 'border-red-500 invalid' : 'border-gray-300'} py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {validationErrors.path
                                ? <div className="text-red-500">Please enter the path this screen opens.</div>
                                : <div className="text-xs text-gray-500 mt-1">The in-app route, e.g. /meal_master/list</div>}
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="menu_group_id" className="block text-sm font-medium leading-6 text-gray-900">Menu Group</label>
                            <div className="mt-2">
                                <Select
                                    {...menuAboveTable}
                                    inputId="menu_group_id"
                                    classNamePrefix="select"
                                    value={menuGroupOptions.find((option) => option.value === formData.menu_group_id) ?? null}
                                    options={menuGroupOptions}
                                    onChange={(selected) => setFormData((prevData) => ({ ...prevData, menu_group_id: selected?.value ?? '' }))}
                                    placeholder="Not in the menu"
                                    isClearable
                                />
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                The heading this screen appears under in the side bar. Leave it empty and the
                                screen still works by its path, it just has nowhere to be listed.
                            </div>
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">Status *</label>
                            <div className="mt-2">
                                <Select
                                    {...menuAboveTable}
                                    inputId="status"
                                    classNamePrefix="select"
                                    value={statusList.find((option) => option.value === Number(formData.status))}
                                    options={statusList}
                                    onChange={(selected) => setFormData((prevData) => ({ ...prevData, status: selected.value }))}
                                    isClearable={false}
                                />
                            </div>
                        </div>
                    </div>
                    {saveError && <div className="text-red-500 mt-4">{saveError}</div>}
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-x-6">
                <Link to={`/${page}/list`}>
                    <button type="button" className="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
                </Link>
                <button
                    onClick={handleSubmit}
                    disabled={popupOpen}
                    type="submit"
                    className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                    Submit
                </button>
            </div>
        </form>
    )
}
